import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'

/**
 * One reading of "can this browser reach the network right now", for the
 * offline banner and for every action that must not fire without it.
 *
 * Sourced from the browser's own `online`/`offline` events. `navigator.onLine`
 * is only trustworthy on the way DOWN — `false` means the OS reports no route,
 * `true` just means an interface is up — so this deliberately does NOT flip to
 * offline on a failed request: a single 502 or a CORS rejection would lock
 * every Save button in the app. Requests that fail for lack of network still
 * get their translated message from errorTranslationInterceptor.
 */
@Injectable({ providedIn: 'root' })
export class NetworkStatusService {
  private readonly onlineState = signal<boolean>(navigator.onLine)

  /** True while the browser reports a network route. */
  readonly isOnline = this.onlineState.asReadonly()
  /** Convenience inverse — reads better in `[disabled]` bindings. */
  readonly isOffline = computed(() => !this.onlineState())
  /** For rxjs consumers; emits the current value on subscribe. */
  readonly isOnline$ = toObservable(this.onlineState)

  constructor() {
    const goOnline = () => this.onlineState.set(true)
    const goOffline = () => this.onlineState.set(false)
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    inject(DestroyRef).onDestroy(() => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    })
  }
}
